import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API } from "@/lib/content";
import { errorText } from "@/lib/errors";

const input = "mt-1 block w-full bg-transparent border-b border-pine/40 py-2 text-base focus:outline-none focus:border-brass transition-colors";
const EMPTY = { name: "", email: "", phone: "", seats: "1", note: "" };

export const ReserveDialog = ({ retreat, onReserved }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const maxSeats = Math.min(4, retreat.seats_left);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/retreats/${retreat.id}/reservations`, {
        ...form, seats: Number(form.seats), phone: form.phone || null, note: form.note || null,
      });
      setDone(data);
      setForm(EMPTY);
      onReserved();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setDone(null); }}>
      <DialogTrigger asChild>
        <button
          type="button"
          disabled={retreat.seats_left < 1}
          className="rounded-full bg-pine text-sage px-6 py-3 transition-colors hover:bg-pine-soft disabled:opacity-40"
          data-testid={`retreat-reserve-button-${retreat.id}`}
        >
          Reserve a seat
        </button>
      </DialogTrigger>
      <DialogContent className="site bg-paper text-pine border-none rounded-2xl max-w-lg" data-testid="reserve-dialog">
        <DialogHeader>
          <DialogTitle className="dp text-3xl font-normal">{done ? "You're on the list" : retreat.title}</DialogTitle>
          <DialogDescription className="text-moss">
            {done ? `We've held ${done.seats} ${done.seats === 1 ? "seat" : "seats"} for you. We'll be in touch with the details.` : "Free to reserve. We'll follow up about the details."}
          </DialogDescription>
        </DialogHeader>
        {done ? (
          <button type="button" onClick={() => setOpen(false)} className="mt-2 rounded-full bg-pine text-sage py-3" data-testid="reserve-done-button">Close</button>
        ) : (
          <form onSubmit={submit} className="grid gap-4" data-testid="reserve-form">
            <label className="text-sm">Your names<input required value={form.name} onChange={set("name")} className={input} data-testid="reserve-name-input" /></label>
            <label className="text-sm">Email<input required type="email" value={form.email} onChange={set("email")} className={input} data-testid="reserve-email-input" /></label>
            <label className="text-sm">Phone (optional)<input type="tel" value={form.phone} onChange={set("phone")} className={input} data-testid="reserve-phone-input" /></label>
            <div className="text-sm">
              Seats
              <Select value={form.seats} onValueChange={(v) => setForm({ ...form, seats: v })}>
                <SelectTrigger className="mt-1 bg-transparent border-pine/40" data-testid="reserve-seats-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: maxSeats }, (_, i) => String(i + 1)).map((n) => (
                    <SelectItem key={n} value={n} data-testid={`reserve-seats-option-${n}`}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <label className="text-sm">Anything we should know? (optional)<textarea rows="2" value={form.note} onChange={set("note")} className={input} data-testid="reserve-note-input" /></label>
            <button type="submit" disabled={saving} className="mt-2 rounded-full bg-pine text-sage py-4 transition-colors hover:bg-pine-soft disabled:opacity-70" data-testid="reserve-submit-button">
              {saving ? "Reserving..." : "Reserve"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
